import {
  AblyGoodsCardActionType,
  AblyGoodsCardChipType,
} from '../../../../api/types';
import {
  isPrimaryCard,
  isVideoGoodsCard,
  isFullVideoGoodsCard,
  type CardEntity,
  type GoodsCardEntity,
  type PrimaryCardEntity,
  type VideoGoodsCardEntity,
  type FullVideoGoodsCardEntity,
} from './types';

import type { GoodsData } from '@banhala/ui-kit';
import { resolveGoodsCardDoAnimate } from '../../../../utils/goodsCardDoAnimate';

export type CardRenderItem =
  | (GoodsData & GoodsCardEntity)
  | PrimaryCardEntity
  | VideoGoodsCardEntity
  | FullVideoGoodsCardEntity;

export const cardRenderItemGenerator = (
  entity: CardEntity[],
): CardRenderItem[] => {
  return entity.flatMap((item): CardRenderItem[] => {
    if (isPrimaryCard(item)) {
      return [item];
    }

    if (isVideoGoodsCard(item) || isFullVideoGoodsCard(item)) {
      return [item];
    }

    const { item: goods, render, logging, type } = item;
    if (!render) {
      return [];
    }

    return [
      {
        type,
        item: goods,
        logging,
        render,
        sno: goods.sno,
        deliveryType: goods.delivery_type,
        thumbnail: {
          src: goods.image,
          doAnimate: resolveGoodsCardDoAnimate(render.data.image.use_animation),
          closedReason: render.data.closed_reason ?? undefined,
          hideLike: !render.data.actions?.includes(
            AblyGoodsCardActionType.Like,
          ),
          badges: (render.data.image_badge_list ?? []).map(badge => ({
            uri: badge.image_url,
            width: badge.image_width,
            height: badge.image_height,
          })),
          emblems: render.data.emblems?.map(emblem => ({
            uri: emblem.image_url,
          })),
        },
        contents: {
          price: {
            price: render.data.price,
            discount: render.data.discount_rate ?? '',
          },
          topNudging: render.data.top_nudging_text ?? undefined,
          priceDescription: render.data.price_description
            ? { name: render.data.price_description.text }
            : undefined,
          cutoff: render.data.cutoff ?? undefined,
          name: {
            goodsName: goods.name,
            marketName: render.data.store_text ?? goods.market_name ?? null,
            nameLineCount: render.data.name_lines,
            showBrand: false,
          },
          imageAd: render.data.has_ad_badge,
          nudge: render.data.nudging_text ?? undefined,
          chips: (render.data.chips ?? []).map(chip => {
            if (chip.type === AblyGoodsCardChipType.DittoText) {
              return {
                type: chip.type,
                name: chip.name,
                customTag: chip.meta
                  ? {
                      bgColor: chip.meta.bg_color,
                      borderColor: chip.meta.border_color,
                      borderWidth: chip.meta.border_width ?? 0,
                      borderRadius: chip.meta.rounded_corner_radius ?? 0,
                      textColor: chip.meta.text_color,
                      textType: chip.meta.text_style,
                    }
                  : undefined,
              };
            }
            return {
              type: chip.type,
              name: chip.name ?? '',
            };
          }),
        },
      },
    ];
  });
};
